/**
 * Trunnion AI - Agent Chat API Client
 */

import apiClient from './client';
import type { ChatMessage, ChatSession } from '@/types/chat.types';

export const chatApi = {
  /**
   * List chat sessions for an agent.
   */
  listSessions: async (agentId: string): Promise<{ sessions: ChatSession[]; total: number }> => {
    const { data } = await apiClient.get(`/agents/${agentId}/chat/sessions`);
    return data;
  },

  /**
   * Get chat session history (all messages).
   */
  getHistory: async (agentId: string, sessionId: string): Promise<ChatMessage[]> => {
    const { data } = await apiClient.get(`/agents/${agentId}/chat/${sessionId}`);
    return data;
  },

  /**
   * Send a message and get the response URL for SSE streaming.
   * Returns the session ID for new sessions.
   */
  sendMessage: async (
    agentId: string,
    message: string,
    sessionId?: string | null,
    classificationLevel = 'unclassified',
  ): Promise<Response> => {
    const baseUrl = apiClient.defaults.baseURL ?? '';
    const token = localStorage.getItem('trunnion-auth-token');

    const response = await fetch(`${baseUrl}/agents/${agentId}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message,
        session_id: sessionId,
        classification_level: classificationLevel,
      }),
    });

    if (!response.ok) {
      throw new Error(`Chat request failed: ${response.statusText}`);
    }

    return response;
  },

  /**
   * Delete a chat session.
   */
  deleteSession: async (sessionId: string): Promise<void> => {
    await apiClient.delete(`/chat/sessions/${sessionId}`);
  },
};
